(() => {
	/* ============================================================
	 * CONFIGURATION
	 * ============================================================ */

	const CONFIG = {
		HOST_SELECTOR: "shopify-forms-embed",
		MAX_WAIT_MS: 15000,

		STYLE_IDS: {
			GLOBAL: "pw-shadow-css-global",
		},

		VALIDATION: {
			DEBOUNCE_MS: 400,
		},
	};

	/* ============================================================
	 * CSS
	 * ============================================================ */

	const SHADOW_CSS_GLOBAL = `
    form[class^="_formFieldset"] >*:nth-child(n+16):not(button) {
        display: none;
    }

    #form-container-ref [class^="_formContainer"] [class^="_gridItem"] {
        padding: 30px;
    }

    #form-container-ref [class^="_formContainer"] [class*="_imageLoading"] {
        display: none;
    }

    form[class^="_formFieldset"] fieldset [class^="_horizontalLayout"] {
        display: flex;
        gap: 2em;
    }

    form[class^="_formFieldset"] fieldset [class^="_horizontalLayout"] label {
        gap: 5px;
    }

    form[class^="_formFieldset"] button[type="submit"] {
        justify-self: center;
        width: auto;
        min-width: 0;
        background: #cad65b !important;
        color: #193a66 !important;
        padding: 16px 44px;
        border-radius: 4px;
        text-decoration: none;
        display: block;
        text-align: center;
        font-weight: 800;
        font-size: 15px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        border: 2px solid #cad65b;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0);
        filter: brightness(1);
        transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease !important;
    }

    form[class^="_formFieldset"] button[type="submit"]:hover {
        filter: brightness(0.88);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }

	/* Kill layout animation without killing loaders/spinners */
	:host *:not(.pw-loader):not(.pw-loader *):not([aria-busy="true"]):not([aria-busy="true"] *):not([class*="_formSubmitButton_"]):not([class*="_formSubmitButton_"] *):not([class*="_spinner_"]) {
	  transition: none !important;
	  animation: none !important;
	}

	:host {
	  --forms-body-font: normal 400 14px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
	  --forms-heading-font: normal 700 22px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
	}

	section[data-sizing="form-wrapper"] {
	  width: 100% !important;
	  max-width: 800px !important;
	  margin-left: auto !important;
	  margin-right: auto !important;
	  position: relative;
	}

	section[data-sizing="form-wrapper"] > div {
	  background: white;
	  border-radius: 10px;
	}

	section[class*="_formHeader_"]{
	  margin-top: 0 !important;
	  text-align: center;
	}

	section[class*="_formHeader_"] h2,
	section[class*="_formHeader_"] p {
	  color: rgb(25, 58, 102) !important;
	  text-align: center;
	  font-size: 36px !important;
	  font-family: inherit !important;
	  font-weight: 700 !important;
	  letter-spacing: normal !important;
	}

	[class*="_formDisclaimer_"] {
	  text-align: center !important;
	  color: rgba(25, 58, 102, 0.6) !important;
	  max-width: 420px;
	  margin-left: auto !important;
	  margin-right: auto !important;
	}

	section[class*="_formHeader_"]::before{
	  content: var(--pw-step-label);
	  z-index: 10;
	  pointer-events: none;
	  text-transform: uppercase;
	  font-weight: 600;
	  font-size: 12px;
	  letter-spacing: 0.08em;
	  color: #2b7bb9;
	  position: absolute;
	  top: 35px;
	  right: 30px;
	  background: rgba(0, 0, 0, .05);
	  padding: 7px 12px;
	  border-radius: 5px;
	}

	/* Grid layout — 6-column base */
	form[data-testid="form"] {
	  display: grid !important;
	  grid-template-columns: repeat(6, 1fr);
	  gap: 30px !important;
	}

	form[data-testid="form"] p,
	form[data-testid="form"] legend,
	form[data-testid="form"] span {
	  color: rgb(25, 58, 102) !important;
	}

	/* Default: all direct children span half width */
	form[data-testid="form"] > div,
	form[data-testid="form"] > fieldset,
	form[data-testid="form"] > button {
	  grid-column: span 3;
	}

	/* div 1: shop-lead-capture wrapper (hidden) */
	form[data-testid="form"] > div:nth-of-type(1) {
	  grid-column: span 6;
	}

	/* div 2: Legal Business Name — full width */
	form[data-testid="form"] > div:nth-of-type(2) {
	  grid-column: span 6;
	}

	/* div 3: Federal Tax ID, div 4: Business Website — half each (default span 3) */

	/* div 5: First Name, div 6: Last Name — half each (default span 3) */
	/* div 7: Email, div 8: Phone — half each (default span 3) */

	/* Submit button — full width so justify-self:center works across the whole form */
	form[data-testid="form"] > button {
	  grid-column: span 6;
	}

	@media screen and (max-width: 860px) {
	  form[data-testid="form"] > div,
	  form[data-testid="form"] > fieldset,
	  form[data-testid="form"] > button {
	    grid-column: span 6 !important;
	  }
	}

  `;


	/* ============================================================
	 * WAIT HELPERS
	 * ============================================================ */

	function now() {
		return typeof performance !== "undefined" && performance.now
			? performance.now()
			: Date.now();
	}

	function waitForHost(timeoutMs = CONFIG.MAX_WAIT_MS) {
		return new Promise((resolve, reject) => {
			const start = now();
			const existing = document.querySelector(CONFIG.HOST_SELECTOR);
			if (existing) return resolve(existing);

			const obs = new MutationObserver(() => {
				const host = document.querySelector(CONFIG.HOST_SELECTOR);
				if (host) {
					obs.disconnect();
					resolve(host);
				} else if (now() - start > timeoutMs) {
					obs.disconnect();
					reject(new Error("Timed out waiting for host"));
				}
			});

			obs.observe(document.documentElement, {
				childList: true,
				subtree: true,
			});
			setTimeout(() => {
				obs.disconnect();
				reject(new Error("Timed out waiting for host"));
			}, timeoutMs);
		});
	}

	function waitForShadowRoot(host, timeoutMs = CONFIG.MAX_WAIT_MS) {
		return new Promise((resolve, reject) => {
			const start = now();
			if (host.shadowRoot) return resolve(host.shadowRoot);

			const obs = new MutationObserver(() => {
				if (host.shadowRoot) {
					obs.disconnect();
					resolve(host.shadowRoot);
				} else if (now() - start > timeoutMs) {
					obs.disconnect();
					reject(new Error("Timed out waiting for shadowRoot"));
				}
			});

			obs.observe(host, { childList: true, subtree: true });
			setTimeout(() => {
				obs.disconnect();
				reject(new Error("Timed out waiting for shadowRoot"));
			}, timeoutMs);
		});
	}


	function isFormPainted(shadowRoot) {
		const form = shadowRoot.querySelector('form[data-testid="form"]');
		if (!form) return false;
		const r = form.getBoundingClientRect();
		return r.width > 0 && r.height > 0;
	}

	function waitForStablePaint(shadowRoot, maxMs = 1500) {
		const start = performance.now();
		return new Promise((resolve) => {
			let last = null;
			let stableFrames = 0;

			const tick = () => {
				if (!isFormPainted(shadowRoot)) {
					if (performance.now() - start > maxMs)
						return resolve(false);
					return requestAnimationFrame(tick);
				}

				const form = shadowRoot.querySelector(
					'form[data-testid="form"]',
				);
				const r = form.getBoundingClientRect();
				const snap = `${Math.round(r.width)}x${Math.round(r.height)}`;

				if (snap === last) stableFrames += 1;
				else stableFrames = 0;

				last = snap;

				if (stableFrames >= 2) return resolve(true);
				if (performance.now() - start > maxMs) return resolve(true);
				requestAnimationFrame(tick);
			};

			requestAnimationFrame(tick);
		});
	}

	/* ============================================================
	 * CSS INJECTION
	 * ============================================================ */

	function ensureStyleEl(shadowRoot, id) {
		let styleEl = shadowRoot.getElementById(id);
		if (!styleEl) {
			styleEl = document.createElement("style");
			styleEl.id = id;
			shadowRoot.appendChild(styleEl);
		}
		return styleEl;
	}

	function setStyleText(styleEl, cssText) {
		if (!styleEl) return;
		if (styleEl.textContent !== cssText) styleEl.textContent = cssText;
	}

	function injectShadowCss(shadowRoot) {
		if (!shadowRoot) return;

		const sGlobal = ensureStyleEl(shadowRoot, CONFIG.STYLE_IDS.GLOBAL);

		setStyleText(sGlobal, SHADOW_CSS_GLOBAL);
	}



	/* ============================================================
	 * SAFARI AUTO-SCROLL PREVENTION
	 * ============================================================ */

	function preventSafariAutoScroll(shadowRoot) {
		const scrollY = window.scrollY;
		let active = true;

		const stop = () => { active = false; };
		window.addEventListener("pointerdown", stop, { once: true });
		window.addEventListener("keydown", stop, { once: true });
		setTimeout(stop, 3000);

		const check = () => {
			if (!active) return;
			const focused = shadowRoot.activeElement;
			if (focused) {
				focused.blur();
				window.scrollTo(0, scrollY);
			}
			requestAnimationFrame(check);
		};
		requestAnimationFrame(check);
	}

	/* ============================================================
	 * INIT
	 * ============================================================ */

	async function init() {
		try {
			const host = await waitForHost();
			const shadowRoot = await waitForShadowRoot(host);

			// CSS first to prevent FOUC
			injectShadowCss(shadowRoot);

			// Guard against Safari auto-scrolling to the phone field
			preventSafariAutoScroll(shadowRoot);

			// Wait for stable first paint, then mark ready
			await waitForStablePaint(shadowRoot);
			host.setAttribute("data-pw-ready", "1");

		} catch (err) {
			console.warn("[forms-shadow] init failed:", err);
		}
	}

	init();
})();
